import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';

import { APIConstants, Constants } from '../../common';
import { IComposition, IGroupResponseMessage, IGuestModeContentsResponse, IGuestScreenModeRequest, IResponseMessage, IScreen, IScreenGroup, IScreenLogs, IScreenList, IScreenResponse, IScreenshot, IScreenShotAndRecordResponse, IScreenValidationResponse, IStopBroadcastData } from '../../models';


@Injectable({
  providedIn: 'root'
})
export class ScreenService {

  constructor(private http: HttpClient) { }

  getAllScreens(): Observable<IScreenResponse> {
    return this.http.get<IScreenResponse>(APIConstants.screens);
  }

  getAllScreenList(): Observable<IScreenList> {
    return this.http.get<IScreenList>(APIConstants.screensList);
  }

  getAllScreensByLimit(limit: number, offset: number, status?: string, search?: string, ordering?: string): Observable<IScreenResponse> {
    let url = APIConstants.screens + '?limit=' + limit + '&offset=' + offset;
    if (status && status !== 'Total') {
        url += '&status=' + status.toLowerCase();
    }
    if (search) {
        url += '&search=' + encodeURIComponent(search);
    }
    if (ordering) {
        url += '&ordering=' + encodeURIComponent(ordering);
    }
    return this.http.get<IScreenResponse>(url);
  }

  addScreen(screen: IScreen): Observable<IResponseMessage> {
    return this.http.post<IResponseMessage>(APIConstants.screens, screen);
  }

  updateScreen(screen_id: number, screen: IScreen): Observable<IResponseMessage> {
    return this.http.put<IResponseMessage>(APIConstants.screens + screen_id + '/', screen);
  }

  updateConnectAndPlay(screen_code: string, enabled: boolean): Observable<IResponseMessage> {
    const payload: IGuestScreenModeRequest = {
      screen_code,
      guest_mode: enabled
    };
    return this.http.post<IResponseMessage>(APIConstants.guestScreenMode, payload);
  }

  bulkUpdateConnectAndPlay(screen_codes: string[], enabled: boolean): Observable<IResponseMessage[]> {
    return forkJoin(screen_codes.map(screenCode => this.updateConnectAndPlay(screenCode, enabled)));
  }

  getGuestModeContents(screen_code: string): Observable<IGuestModeContentsResponse> {
    return this.http.get<IGuestModeContentsResponse>(APIConstants.guestModeContents + '?screen_code=' + encodeURIComponent(screen_code));
  }

  deleteScreenById(screen_id: number): Observable<IResponseMessage> {
    return this.http.delete<IResponseMessage>(APIConstants.screens + screen_id + '/');
  }

  getScreenById(screen_id: number): Observable<IScreen> {
    return this.http.get<IScreen>(APIConstants.screens + screen_id + '/');
  }

  getScreenLogs(): Observable<IScreenLogs[]> {
    return this.http.get<IScreenLogs[]>(APIConstants.screenLogs);
  }

  getScreenLogsByCode(screenCode: string): Observable<IScreenLogs[]> {
    return this.http.get<IScreenLogs[]>(APIConstants.screenLogs + '?screen_code=' + screenCode);
  }

  getAllScreenGroups(): Observable<IScreenGroup[]> {
    return this.http.get<IScreenGroup[]>(APIConstants.screenGroup);
  }

  addScreenGroup(screenGroup: IScreenGroup): Observable<IGroupResponseMessage> {
    return this.http.post<IGroupResponseMessage>(APIConstants.screenGroup, screenGroup);
  }

  updateScreenGroup(screen_group_id: number, screenGroup: IScreenGroup): Observable<IGroupResponseMessage> {
    return this.http.put<IGroupResponseMessage>(APIConstants.screenGroup + screen_group_id + '/', screenGroup);
  }

  deleteScreenGroupById(screen_group_id: number): Observable<IResponseMessage> {
    return this.http.delete<IResponseMessage>(APIConstants.screenGroup + screen_group_id + '/');
  }

  getScreenGroupById(screen_group_id: number): Observable<IScreenGroup> {
    return this.http.get<IScreenGroup>(APIConstants.screenGroup + screen_group_id + '/');
  }

  deleteMultipleScreens(screen_ids: number[]): Observable<IResponseMessage> {
    const options = {
      body: { screen_ids: screen_ids } // Pass body here
    };

    return this.http.delete<IResponseMessage>(APIConstants.deleteMultipleScreens, options);
  }

  takeScreenShot(screen_id: number, screen_code: string, type: string): Observable<IResponseMessage> {
    return this.http.post<IResponseMessage>(type === Constants.screenCaptureType.screenshot ? APIConstants.screenshot : APIConstants.screenRecording, { screen_id: screen_id, screen_code: screen_code, type: type });
  }

  getScreenshotDeletionDayById(business_id: number): Observable<number> {
    return this.http.get<number>(APIConstants.screenshotDeletionDays + `?business_id=${business_id}`);
  }

  getScreenshotDeletionDaysData(): Observable<number[]> {
    return this.http.get<number[]>(APIConstants.screenshotDeletionDays);
  }

  screenshotDeletionDaysUpdate(days: number): Observable<IResponseMessage> {
    return this.http.put<IResponseMessage>(APIConstants.screenshotDeletionDays, { days: days });
  }

  deleteScreenshotById(id: number, type: string): Observable<IResponseMessage> {
    return this.http.delete<IResponseMessage>((type === Constants.screenCaptureType.screenshot ? APIConstants.screenshot : APIConstants.screenRecording) + id + '/');
  }

  getAllScreenshotsByScreenId(screen_id: number, screen_code: string): Observable<IScreenShotAndRecordResponse> {
    return this.http.get<IScreenShotAndRecordResponse>(APIConstants.screenshot + '?screen_id=' + screen_id + '&screen_code=' + screen_code);
  }

  getAllPlaylistByScreenId(screen_id: number, start_date: string, start_time: string): Observable<IComposition[]> {
    return this.http.get<IComposition[]>(APIConstants.screenPlaylist + '?screen_id=' + screen_id + '&start_date=' + start_date + '&start_time=' + start_time);
  }

  getScreenshotById(screenshot_id: number): Observable<IScreenshot> {
    return this.http.get<IScreenshot>(APIConstants.screenshot + '?screenshot_id=' + screenshot_id);
  }

  validateScreenDelete(screen_ids: number[]): Observable<IScreenValidationResponse> {
    return this.http.post<IScreenValidationResponse>(APIConstants.validateScreenDelete, { 'screen_ids': screen_ids });
  }

  stopActiveSchedule(stopBrodcastData: IStopBroadcastData): Observable<string> {
    return this.http.post<string>(APIConstants.stopActiveSchedule, stopBrodcastData);
  }

  forceExitApp(screen_id: number): Observable<string> {
    return this.http.post<string>(APIConstants.forceExitApp, { screen_id: screen_id });
  }

  clearPlayerCache(screen_code: string): Observable<IResponseMessage> {
    return this.http.post<IResponseMessage>(APIConstants.clearPlayerCache, { screen_code: screen_code });
  }

}
